import React, { useState } from 'react';
import { motion } from 'framer-motion';
import EnterpriseHeroSection from '@/components/EnterpriseHeroSection';
import TestimonialSection from '@/components/TestimonialSection';
import PersonaToggle from '@/components/PersonaToggle';
import AccountabilitySection from '@/components/AccountabilitySection';
import FeatureShowcase from '@/components/FeatureShowcase';
import AllInOnePlatform from '@/components/AllInOnePlatform';
import OutputGallery from '@/components/OutputGallery';
import MoatSection from '@/components/MoatSection';
import CompetitorComparison from '@/components/CompetitorComparison';
import SecurityFooter from '@/components/SecurityFooter';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Check, Shield, Users, Zap, Award, Building, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EnterpriseLanding = () => {
  const navigate = useNavigate();

  // Handler for demo signin
  const handleDemoSignIn = () => {
    // Navigate to demo signin or trigger demo modal
    navigate('/login?demo=true');
  };

  // Handler for book demo
  const handleBookDemo = () => {
    navigate('/book-demo');
  };

  // Enterprise Benefits Section (replaces pricing)
  const EnterpriseBenefitsSection = () => (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-800">
            Enterprise Solution
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Built for Scale, Designed for Success
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience the power of AI-driven outreach with enterprise-grade features and support
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Volume & Scale */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl p-8"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Unlimited Scale</h3>
            <p className="text-gray-600 mb-4">
              Send millions of personalized emails monthly with our robust infrastructure
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">Custom volume limits</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">Dedicated IP pools</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">Priority sending queues</span>
              </li>
            </ul>
          </motion.div>

          {/* Security & Compliance */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-2xl p-8"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Enterprise Security</h3>
            <p className="text-gray-600 mb-4">
              Bank-level security with complete compliance and data protection
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">SOC 2 Type II certified</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">GDPR & CCPA compliant</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">SSO & 2FA support</span>
              </li>
            </ul>
          </motion.div>

          {/* Support & Service */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-2xl p-8"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Award className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">White Glove Service</h3>
            <p className="text-gray-600 mb-4">
              Dedicated support team ensuring your success every step of the way
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">Dedicated account manager</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">24/7 priority support</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">Custom onboarding</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );

  // Enterprise Features Grid
  const EnterpriseFeatures = () => (
    <section className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Everything You Need to Scale
          </h2>
          <p className="text-xl text-gray-600">
            Comprehensive features designed for enterprise success
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Building, title: "Multi-team Management", desc: "Manage multiple teams and campaigns from one dashboard" },
            { icon: Lock, title: "Advanced Permissions", desc: "Granular role-based access control for your entire organization" },
            { icon: Users, title: "Unlimited Users", desc: "Add your entire team without worrying about seat limits" },
            { icon: Zap, title: "API Access", desc: "Full API access for custom integrations and workflows" },
            { icon: Shield, title: "Custom Compliance", desc: "Meet your specific compliance and regulatory requirements" },
            { icon: Award, title: "SLA Guarantee", desc: "99.9% uptime guarantee with enterprise SLA" }
          ].map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <feature.icon className="w-10 h-10 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600">
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // FAQ Section without pricing questions
  const EnterpriseFAQSection = () => (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about Munger.ai Enterprise
          </p>
        </div>

        <div className="space-y-6">
          {[
            {
              q: "How quickly can we get started?",
              a: "Enterprise onboarding typically takes 3-5 business days. Our team will work with you to ensure smooth integration with your existing systems and workflows."
            },
            {
              q: "Can we integrate with our existing CRM?",
              a: "Yes! We offer native integrations with Salesforce, HubSpot, Pipedrive, and more. Our API also allows for custom integrations with any system."
            },
            {
              q: "What kind of support do enterprise customers receive?",
              a: "Enterprise customers receive dedicated account management, 24/7 priority support, custom onboarding, and quarterly business reviews."
            },
            {
              q: "How does the AI personalization work?",
              a: "Our AI analyzes your ideal customer profile and automatically researches prospects to create highly personalized messages that resonate with each recipient."
            },
            {
              q: "What security certifications do you have?",
              a: "We're SOC 2 Type II certified, GDPR and CCPA compliant, and undergo regular third-party security audits. We also support SSO and custom security requirements."
            },
            {
              q: "Can we customize the platform for our needs?",
              a: "Absolutely. Enterprise customers can request custom features, integrations, and workflows tailored to their specific requirements."
            }
          ].map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {faq.q}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {faq.a}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // Final CTA Section
  const EnterpriseCTASection = () => (
    <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-700">
      <div className="max-w-4xl mx-auto px-6 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Badge className="mb-6 bg-white/20 text-white border-white/30">
            <Building className="w-4 h-4 mr-1" />
            Enterprise Solution
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            See Munger.ai in Action
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join Fortune 500 companies using AI to transform their outreach
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button 
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              onClick={handleDemoSignIn}
            >
              Try Demo Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <Button 
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-semibold"
              onClick={handleBookDemo}
            >
              Schedule Demo Call
            </Button>
          </div>
          
          <div className="flex items-center justify-center gap-6 text-sm text-blue-100">
            <span>✓ No credit card required</span>
            <span>✓ Full platform access</span>
            <span>✓ See real results</span>
          </div>
        </motion.div>
      </div>
    </section>
  );

  return (
    <>
      <div className="bg-gradient-to-br from-white to-blue-50">
        <EnterpriseHeroSection 
          onDemoClick={handleDemoSignIn}
          onBookDemo={handleBookDemo}
        />
        <TestimonialSection />
        <PersonaToggle />
        <AccountabilitySection />
        <FeatureShowcase />
        <AllInOnePlatform onStartClick={handleDemoSignIn} />
        <OutputGallery />
        <EnterpriseBenefitsSection />
        <EnterpriseFeatures />
        <CompetitorComparison onStartClick={handleDemoSignIn} />
        <EnterpriseFAQSection />
        <EnterpriseCTASection />
        <SecurityFooter />
      </div>
    </>
  );
};

export default EnterpriseLanding;